const toNumber = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const text = String(value ?? "0").trim();
  const normalized = text.includes(",")
    ? text.replace(/\./g, "").replace(",", ".")
    : text;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
};

const cents = (value) => Math.round((value + Number.EPSILON) * 100);
const amount = (valueInCents) => valueInCents / 100;

function distributeCents(totalCents, weights) {
  if (!weights.length) return [];
  if (totalCents === 0) return weights.map(() => 0);
  const weightTotal = weights.reduce((sum, weight) => sum + Math.max(0, weight), 0);
  if (weightTotal <= 0) {
    if (totalCents !== 0) throw new RangeError("ADJUSTMENT_WITHOUT_BASE");
    return weights.map(() => 0);
  }

  const exact = weights.map((weight) => totalCents * Math.max(0, weight) / weightTotal);
  const result = exact.map(Math.floor);
  let remaining = totalCents - result.reduce((sum, value) => sum + value, 0);
  const order = exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .sort((left, right) => right.remainder - left.remainder || left.index - right.index);
  for (let index = 0; index < remaining; index += 1) result[order[index].index] += 1;
  return result;
}

function roundedGroupCents(totalCents, rawGroups) {
  const result = rawGroups.map((value) => cents(value));
  let difference = totalCents - result.reduce((sum, value) => sum + value, 0);
  for (let index = 0; difference !== 0; index = (index + 1) % Math.max(1, result.length)) {
    const step = difference > 0 ? 1 : -1;
    result[index] += step;
    difference -= step;
  }
  return result;
}

function adjustmentByBase(mode, rawValue, baseCents, label) {
  const value = toNumber(rawValue);
  if (value < 0) throw new RangeError(`${label}_NEGATIVE`);
  if (mode !== "PERCENT" && mode !== "AMOUNT") throw new RangeError(`${label}_MODE`);
  if (mode === "PERCENT") {
    if (label === "DISCOUNT" && value > 100) throw new RangeError("DISCOUNT_PERCENT_RANGE");
    return baseCents.map((base) => Math.round(base * value / 100));
  }
  return distributeCents(cents(value), baseCents);
}

export function calculateCommercialEconomics(lines, adjustments = {}) {
  const economicLines = (lines || []).filter((line) => !line.note);
  const groupedRaw = new Map();
  let subtotalRaw = 0;
  let netRaw = 0;
  let costRaw = 0;

  for (const line of economicLines) {
    const quantity = toNumber(line.qty);
    const price = toNumber(line.price);
    const discount = toNumber(line.discount);
    const rate = toNumber(line.igic);
    const gross = quantity * price;
    const net = gross * (1 - discount / 100);
    subtotalRaw += gross;
    netRaw += net;
    costRaw += quantity * toNumber(line.cost);
    groupedRaw.set(rate, (groupedRaw.get(rate) || 0) + net);
  }

  const groups = [...groupedRaw.entries()].sort((left, right) => left[0] - right[0]);
  const totalBaseCents = cents(netRaw);
  const baseCents = roundedGroupCents(totalBaseCents, groups.map(([, base]) => base));
  const discountMode = adjustments.globalDiscountMode || "PERCENT";
  const expenseMode = adjustments.expenseMode || "PERCENT";
  const discountCents = adjustmentByBase(discountMode, adjustments.globalDiscountValue || 0, baseCents, "DISCOUNT");
  const expenseCents = adjustmentByBase(expenseMode, adjustments.expenseValue || 0, baseCents, "EXPENSE");

  const globalDiscountCents = discountCents.reduce((sum, value) => sum + value, 0);
  if (globalDiscountCents > 0 && globalDiscountCents > totalBaseCents) {
    throw new RangeError("DISCOUNT_EXCEEDS_BASE");
  }

  const bases = groups.map(([rate], index) => {
    const adjustedCents = baseCents[index] - discountCents[index] + expenseCents[index];
    const taxCents = Math.round(adjustedCents * rate / 100);
    return {
      rate,
      base: amount(baseCents[index]),
      globalDiscount: amount(discountCents[index]),
      expenses: amount(expenseCents[index]),
      adjustedBase: amount(adjustedCents),
      tax: amount(taxCents),
    };
  });

  const subtotal = amount(cents(subtotalRaw));
  const lineDiscount = amount(cents(subtotalRaw) - totalBaseCents);
  const globalDiscount = amount(globalDiscountCents);
  const expenses = amount(expenseCents.reduce((sum, value) => sum + value, 0));
  const base = amount(bases.reduce((sum, group) => sum + cents(group.adjustedBase), 0));
  const tax = amount(bases.reduce((sum, group) => sum + cents(group.tax), 0));
  const cost = amount(cents(costRaw));
  const profit = amount(cents(base - cost));

  return {
    subtotal,
    lineDiscount,
    globalDiscount,
    expenses,
    base,
    tax,
    total: amount(cents(base + tax)),
    cost,
    profit,
    margin: base ? profit / base * 100 : 0,
    bases,
  };
}

export function prorateFixedAdjustment(value, convertedBase, sourceBase) {
  const source = toNumber(sourceBase);
  if (source <= 0) return 0;
  return amount(cents(toNumber(value) * Math.max(0, toNumber(convertedBase)) / source));
}
